import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Edit, Plus, Trash2, Upload } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const AdminCMS = () => {
  const [settings, setSettings] = useState({ site_name: 'Rejumist™', site_description: '', contact_email: '', contact_phone: '', contact_address: '', social_facebook: '', social_instagram: '', social_twitter: '' });
  const [hero, setHero] = useState({ heading: '', subheading: '', button_text: '', button_link: '', background_image: '' });
  const [brandStory, setBrandStory] = useState({ heading: '', body_paragraph_1: '', body_paragraph_2: '', image_url: '', button_text: '', button_link: '' });
  const [brandStoryImageUploading, setBrandStoryImageUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pages, setPages] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [newPage, setNewPage] = useState({ slug: '', title: '', content: '' });
  const [editingBlog, setEditingBlog] = useState(null);
  const [newBlog, setNewBlog] = useState({ title: '', slug: '', content: '', excerpt: '', image_url: '', author: 'Rejumist™' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', image_url: '', images: [] });
  const [footer, setFooter] = useState({ brand_tagline: '', legal_name: '', copyright_text: '', fine_print: '', columns: [], social_links: { facebook_url: '', instagram_url: '' } });
  const [cartShelf, setCartShelf] = useState({ enabled: true, heading: '', subheading: '', target_qty: 5, cta_template: '', product_ids: [], basket_discount_percent: 0, basket_min_qty: 5 });
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, heroRes, brandStoryRes, categoriesRes, pagesRes, blogsRes, footerRes, recsRes, productsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/settings`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/hero`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/brand-story`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/pages`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/blogs`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cms/footer`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cart/recommendations`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products`),
      ]);
      setSettings(settingsRes.data);
      setHero(heroRes.data);
      setBrandStory(brandStoryRes.data);
      setCategories(categoriesRes.data);
      setPages(pagesRes.data);
      setBlogs(blogsRes.data);
      setFooter({
        brand_tagline: footerRes.data.brand_tagline || '',
        legal_name: footerRes.data.legal_name || '',
        copyright_text: footerRes.data.copyright_text || '',
        fine_print: footerRes.data.fine_print || '',
        columns: (footerRes.data.columns || []).map((c) => ({ title: c.title || '', links: (c.links || []).map((l) => ({ ...l, visible: l.visible !== false })) })),
        social_links: {
          facebook_url: footerRes.data.social_links?.facebook_url || '',
          instagram_url: footerRes.data.social_links?.instagram_url || '',
        },
      });
      setCartShelf({
        enabled: recsRes.data.enabled !== false,
        heading: recsRes.data.heading || '',
        subheading: recsRes.data.subheading || '',
        target_qty: recsRes.data.target_qty || 5,
        cta_template: recsRes.data.cta_template || '',
        product_ids: recsRes.data.product_ids || [],
        basket_discount_percent: recsRes.data.basket_discount_percent || 0,
        basket_min_qty: recsRes.data.basket_min_qty || 0,
      });
      setAllProducts(productsRes.data || []);
    } catch {
      console.error('Failed to fetch CMS data');
    }
  };

  const saveCartShelf = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cart-recommendations`, {
        ...cartShelf,
        target_qty: parseInt(cartShelf.target_qty) || 5,
        basket_discount_percent: Number(cartShelf.basket_discount_percent) || 0,
        basket_min_qty: parseInt(cartShelf.basket_min_qty) || 0,
      }, { withCredentials: true });
      toast.success('Cart shelf saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const toggleShelfProduct = (pid) => {
    const set = new Set(cartShelf.product_ids);
    if (set.has(pid)) set.delete(pid); else set.add(pid);
    setCartShelf({ ...cartShelf, product_ids: Array.from(set).slice(0, 4) });
  };

  const saveFooter = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/footer`, footer, { withCredentials: true });
      toast.success('Footer saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save footer'); }
  };
  const addFooterColumn = () => setFooter({ ...footer, columns: [...footer.columns, { title: 'New Column', links: [{ label: '', url: '', visible: true, external: false }] }] });
  const removeFooterColumn = (ci) => setFooter({ ...footer, columns: footer.columns.filter((_, i) => i !== ci) });
  const updateColumnTitle = (ci, title) => {
    const cols = [...footer.columns]; cols[ci] = { ...cols[ci], title }; setFooter({ ...footer, columns: cols });
  };
  const addFooterLink = (ci) => {
    const cols = [...footer.columns]; cols[ci] = { ...cols[ci], links: [...cols[ci].links, { label: '', url: '', visible: true, external: false }] }; setFooter({ ...footer, columns: cols });
  };
  const updateFooterLink = (ci, li, field, value) => {
    const cols = [...footer.columns]; const links = [...cols[ci].links]; links[li] = { ...links[li], [field]: value }; cols[ci] = { ...cols[ci], links }; setFooter({ ...footer, columns: cols });
  };
  const removeFooterLink = (ci, li) => {
    const cols = [...footer.columns]; cols[ci] = { ...cols[ci], links: cols[ci].links.filter((_, i) => i !== li) }; setFooter({ ...footer, columns: cols });
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/settings`, settings, { withCredentials: true });
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const saveHero = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/hero`, hero, { withCredentials: true });
      toast.success('Hero section saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const saveBrandStory = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/brand-story`, brandStory, { withCredentials: true });
      toast.success('Brand story saved');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleBrandStoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBrandStoryImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setBrandStory((prev) => ({ ...prev, image_url: url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBrandStoryImageUploading(false);
      e.target.value = '';
    }
  };

  const savePage = async () => {
    try {
      if (editingPage) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/pages/${editingPage.slug}`, editingPage, { withCredentials: true });
        toast.success('Page updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cms/pages`, newPage, { withCredentials: true });
        toast.success('Page created');
        setNewPage({ slug: '', title: '', content: '' });
      }
      setEditingPage(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deletePage = async (slug) => {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/cms/pages/${slug}`, { withCredentials: true });
      toast.success('Page deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveCategory = async () => {
    try {
      const catData = editingCategory || newCategory;
      if (!catData.name) { toast.error('Category name is required'); return; }
      if (editingCategory) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories/${editingCategory.category_id}`, catData, { withCredentials: true });
        toast.success('Category updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories`, catData, { withCredentials: true });
        toast.success('Category created');
        setNewCategory({ name: '', description: '', image_url: '', images: [] });
      }
      setEditingCategory(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/cms/categories/${categoryId}`, { withCredentials: true });
      toast.success('Category deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const getCatImages = () => (editingCategory ? editingCategory.images || [] : newCategory.images || []);
  const setCatImages = (images) => {
    if (editingCategory) setEditingCategory(prev => ({ ...prev, images, image_url: images[0] || prev.image_url }));
    else setNewCategory(prev => ({ ...prev, images, image_url: images[0] || prev.image_url }));
  };
  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const current = getCatImages();
    if (current.length >= 5) { toast.error('Maximum 5 images allowed'); return; }
    setCategoryImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setCatImages([...current, url]);
      toast.success(`Image ${current.length + 1}/5 uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setCategoryImageUploading(false);
      e.target.value = '';
    }
  };
  const handleAddCatImageUrl = () => {
    const current = getCatImages();
    if (current.length >= 5) { toast.error('Maximum 5 images allowed'); return; }
    const url = editingCategory ? editingCategory.image_url : newCategory.image_url;
    if (!url) { toast.error('Enter a URL first'); return; }
    if (!/^https?:\/\/.+/i.test(url)) { toast.error('Invalid URL. Must start with http:// or https://'); return; }
    if (!current.includes(url)) {
      setCatImages([...current, url]);
      toast.success(`Image ${current.length + 1}/5 added`);
    }
  };
  const removeCatImage = (idx) => {
    const newImages = getCatImages().filter((_, i) => i !== idx);
    setCatImages(newImages);
  };

  const saveBlog = async () => {
    try {
      const blogData = editingBlog || newBlog;
      if (editingBlog) {
        await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${editingBlog.slug}`, blogData, { withCredentials: true });
        toast.success('Blog updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/blogs`, blogData, { withCredentials: true });
        toast.success('Blog created');
        setNewBlog({ title: '', slug: '', content: '', excerpt: '', image_url: '', author: 'Rejumist™' });
      }
      setEditingBlog(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteBlog = async (slug) => {
    if (!window.confirm('Delete this blog?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/blogs/${slug}`, { withCredentials: true });
      toast.success('Blog deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="container mx-auto px-4 py-10 pt-16">
        <h1 className="text-3xl font-black font-heading mb-8" data-testid="cms-heading">Content Management</h1>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto">
            <TabsTrigger value="settings">Settings</TabsTrigger>
            <TabsTrigger value="hero">Hero</TabsTrigger>
            <TabsTrigger value="brand-story" data-testid="cms-brand-story-tab">Brand Story</TabsTrigger>
            <TabsTrigger value="pages">Pages</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="blogs">Blogs</TabsTrigger>
            <TabsTrigger value="footer" data-testid="cms-footer-tab">Footer</TabsTrigger>
            <TabsTrigger value="cartshelf" data-testid="cms-cartshelf-tab">Cart Shelf</TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="text-lg font-bold font-heading">Site Settings</h2>
              <div>
                <Label>Site Name</Label>
                <Input value={settings.site_name} onChange={(e) => setSettings({...settings, site_name: e.target.value})} placeholder="Rejumist™" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={settings.site_description} onChange={(e) => setSettings({...settings, site_description: e.target.value})} placeholder="Premium artisan teas" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input value={settings.contact_email} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} placeholder="hello@ind-petrichor.in" /></div>
                <div><Label>Phone</Label><Input value={settings.contact_phone} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} placeholder="+91 9876543210" /></div>
              </div>
              <div><Label>Address</Label><Textarea value={settings.contact_address} onChange={(e) => setSettings({...settings, contact_address: e.target.value})} placeholder="Tea Estate Road, Darjeeling" /></div>
              <Button onClick={saveSettings} data-testid="save-settings-button"><Save className="mr-2 h-4 w-4" /> Save</Button>
            </div>
          </TabsContent>

          <TabsContent value="hero" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="text-lg font-bold font-heading">Hero Section</h2>
              <div><Label>Heading</Label><Input value={hero.heading} onChange={(e) => setHero({...hero, heading: e.target.value})} placeholder="Where Nature Meets Your Cup" /></div>
              <div><Label>Subheading</Label><Textarea value={hero.subheading} onChange={(e) => setHero({...hero, subheading: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Button Text</Label><Input value={hero.button_text} onChange={(e) => setHero({...hero, button_text: e.target.value})} /></div>
                <div><Label>Button Link</Label><Input value={hero.button_link} onChange={(e) => setHero({...hero, button_link: e.target.value})} /></div>
              </div>
              <div><Label>Background Image URL</Label><Input value={hero.background_image} onChange={(e) => setHero({...hero, background_image: e.target.value})} /></div>
              <Button onClick={saveHero} data-testid="save-hero-button"><Save className="mr-2 h-4 w-4" /> Save</Button>
            </div>
          </TabsContent>

          <TabsContent value="brand-story" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4" data-testid="brand-story-form">
              <h2 className="text-lg font-bold font-heading">Brand Story (Homepage &quot;Our Story&quot; section)</h2>
              <div>
                <Label>Heading <span className="text-xs text-muted-foreground">(supports &lt;br /&gt; for line breaks)</span></Label>
                <Input value={brandStory.heading} onChange={(e) => setBrandStory({...brandStory, heading: e.target.value})} placeholder="From Earth.<br />For Ritual." data-testid="brand-story-heading-input" />
              </div>
              <div>
                <Label>Body Paragraph 1</Label>
                <Textarea rows={3} value={brandStory.body_paragraph_1} onChange={(e) => setBrandStory({...brandStory, body_paragraph_1: e.target.value})} data-testid="brand-story-p1-input" />
              </div>
              <div>
                <Label>Body Paragraph 2</Label>
                <Textarea rows={3} value={brandStory.body_paragraph_2} onChange={(e) => setBrandStory({...brandStory, body_paragraph_2: e.target.value})} data-testid="brand-story-p2-input" />
              </div>
              <div>
                <Label>Image</Label>
                <div className="flex gap-2 items-center">
                  <Input value={brandStory.image_url} onChange={(e) => setBrandStory({...brandStory, image_url: e.target.value})} placeholder="Paste an image URL or upload" className="flex-1" data-testid="brand-story-image-url" />
                  <input id="brand-story-upload" type="file" accept="image/*" hidden onChange={handleBrandStoryImageUpload} data-testid="brand-story-image-file" />
                  <Button type="button" variant="outline" disabled={brandStoryImageUploading} onClick={() => document.getElementById('brand-story-upload').click()} data-testid="brand-story-upload-button">
                    {brandStoryImageUploading ? 'Uploading…' : 'Upload'}
                  </Button>
                </div>
                {brandStory.image_url && (
                  <img src={brandStory.image_url} alt="brand story preview" className="mt-3 rounded-lg border w-64 aspect-[4/3] object-cover" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Button Text</Label><Input value={brandStory.button_text} onChange={(e) => setBrandStory({...brandStory, button_text: e.target.value})} data-testid="brand-story-btn-text" /></div>
                <div><Label>Button Link</Label><Input value={brandStory.button_link} onChange={(e) => setBrandStory({...brandStory, button_link: e.target.value})} placeholder="/page/about" data-testid="brand-story-btn-link" /></div>
              </div>
              <Button onClick={saveBrandStory} data-testid="save-brand-story-button"><Save className="mr-2 h-4 w-4" /> Save</Button>
            </div>
          </TabsContent>

          <TabsContent value="pages" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="text-lg font-bold font-heading">{editingPage ? 'Edit Page' : 'Create Page'}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Slug</Label><Input value={editingPage ? editingPage.slug : newPage.slug} onChange={(e) => editingPage ? setEditingPage({...editingPage, slug: e.target.value}) : setNewPage({...newPage, slug: e.target.value})} disabled={!!editingPage} /></div>
                <div><Label>Title</Label><Input value={editingPage ? editingPage.title : newPage.title} onChange={(e) => editingPage ? setEditingPage({...editingPage, title: e.target.value}) : setNewPage({...newPage, title: e.target.value})} /></div>
              </div>
              <div><Label>Content (HTML)</Label><Textarea rows={8} value={editingPage ? editingPage.content : newPage.content} onChange={(e) => editingPage ? setEditingPage({...editingPage, content: e.target.value}) : setNewPage({...newPage, content: e.target.value})} /></div>
              <div className="flex gap-2">
                <Button onClick={savePage} data-testid="save-page-button"><Save className="mr-2 h-4 w-4" /> {editingPage ? 'Update' : 'Create'}</Button>
                {editingPage && <Button variant="outline" onClick={() => setEditingPage(null)}>Cancel</Button>}
              </div>
              <div className="space-y-2 mt-4">
                {pages.map((page) => (
                  <div key={page.slug} className="flex items-center justify-between p-3 rounded-lg border border-[#E0E0E0]">
                    <div><p className="font-medium text-sm">{page.title}</p><p className="text-xs text-muted-foreground">/{page.slug}</p></div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setEditingPage(page)}><Edit className="h-3 w-3" /></Button>
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => deletePage(page.slug)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="categories" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="text-lg font-bold font-heading">{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
              <div>
                <div><Label>Name</Label><Input value={editingCategory ? editingCategory.name : newCategory.name} onChange={(e) => editingCategory ? setEditingCategory({...editingCategory, name: e.target.value}) : setNewCategory({...newCategory, name: e.target.value})} placeholder="e.g. Herbal Tea" /></div>
              </div>
              <div><Label>Description</Label><Input value={editingCategory ? editingCategory.description : newCategory.description} onChange={(e) => editingCategory ? setEditingCategory({...editingCategory, description: e.target.value}) : setNewCategory({...newCategory, description: e.target.value})} placeholder="Short description" /></div>
              <div>
                <Label>Images (up to 5)</Label>
                <div className="flex gap-2 mb-2">
                  <Input value={editingCategory ? editingCategory.image_url : newCategory.image_url} onChange={(e) => editingCategory ? setEditingCategory({...editingCategory, image_url: e.target.value}) : setNewCategory({...newCategory, image_url: e.target.value})} placeholder="Paste URL and click Add, or Upload" className="flex-1" />
                  <Button type="button" variant="outline" size="sm" className="flex-shrink-0" onClick={handleAddCatImageUrl} disabled={getCatImages().length >= 5}>Add</Button>
                  <Button type="button" variant="outline" size="sm" className="flex-shrink-0 relative" disabled={categoryImageUploading || getCatImages().length >= 5}>
                    <Upload className="h-4 w-4 mr-1" /> {categoryImageUploading ? '...' : 'Upload'}
                    <input type="file" accept="image/*" onChange={handleCategoryImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2">{getCatImages().length}/5 images added</p>
                {getCatImages().length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {getCatImages().map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={img} alt={`Image ${idx + 1}`} className="h-16 w-16 object-cover rounded-lg border border-[#E0E0E0]" />
                        <button type="button" onClick={() => removeCatImage(idx)} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
                        {idx === 0 && <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-primary/80 text-white rounded-b-lg">Cover</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={saveCategory}><Save className="mr-2 h-4 w-4" /> {editingCategory ? 'Update' : 'Create'}</Button>
                {editingCategory && <Button variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>}
              </div>
              <div className="space-y-2 mt-4">
                {categories.map((cat) => (
                  <div key={cat.category_id} className="flex items-center justify-between p-3 rounded-lg border border-[#E0E0E0]">
                    <div>
                      <p className="font-medium text-sm">{cat.name}</p>
                      <p className="text-xs text-muted-foreground">{cat.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setEditingCategory(cat)}><Edit className="h-3 w-3" /></Button>
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => deleteCategory(cat.category_id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="blogs" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <h2 className="text-lg font-bold font-heading">{editingBlog ? 'Edit Blog' : 'Create Blog'}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={editingBlog ? editingBlog.title : newBlog.title} onChange={(e) => editingBlog ? setEditingBlog({...editingBlog, title: e.target.value}) : setNewBlog({...newBlog, title: e.target.value})} /></div>
                <div><Label>Slug</Label><Input value={editingBlog ? editingBlog.slug : newBlog.slug} onChange={(e) => editingBlog ? setEditingBlog({...editingBlog, slug: e.target.value}) : setNewBlog({...newBlog, slug: e.target.value})} disabled={!!editingBlog} /></div>
              </div>
              <div><Label>Excerpt</Label><Input value={editingBlog ? editingBlog.excerpt : newBlog.excerpt} onChange={(e) => editingBlog ? setEditingBlog({...editingBlog, excerpt: e.target.value}) : setNewBlog({...newBlog, excerpt: e.target.value})} /></div>
              <div><Label>Image URL</Label><Input value={editingBlog ? editingBlog.image_url : newBlog.image_url} onChange={(e) => editingBlog ? setEditingBlog({...editingBlog, image_url: e.target.value}) : setNewBlog({...newBlog, image_url: e.target.value})} /></div>
              <div><Label>Content (HTML)</Label><Textarea rows={8} value={editingBlog ? editingBlog.content : newBlog.content} onChange={(e) => editingBlog ? setEditingBlog({...editingBlog, content: e.target.value}) : setNewBlog({...newBlog, content: e.target.value})} /></div>
              <div className="flex gap-2">
                <Button onClick={saveBlog}><Save className="mr-2 h-4 w-4" /> {editingBlog ? 'Update' : 'Create'}</Button>
                {editingBlog && <Button variant="outline" onClick={() => setEditingBlog(null)}>Cancel</Button>}
              </div>
              <div className="space-y-2 mt-4">
                {blogs.map((blog) => (
                  <div key={blog.blog_id} className="flex items-center justify-between p-3 rounded-lg border border-[#E0E0E0]">
                    <div><p className="font-medium text-sm">{blog.title}</p><p className="text-xs text-muted-foreground">/{blog.slug}</p></div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setEditingBlog(blog)}><Edit className="h-3 w-3" /></Button>
                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => deleteBlog(blog.slug)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="footer" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold font-heading">Footer Branding</h2>
                <Button size="sm" onClick={saveFooter} data-testid="save-footer-btn"><Save className="mr-2 h-4 w-4" /> Save Footer</Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label className="text-xs">Brand tagline</Label><Input value={footer.brand_tagline} onChange={(e) => setFooter({...footer, brand_tagline: e.target.value})} placeholder="Where nature meets your cup..." data-testid="footer-tagline-input" /></div>
                <div><Label className="text-xs">Legal name</Label><Input value={footer.legal_name} onChange={(e) => setFooter({...footer, legal_name: e.target.value})} placeholder="IND-PETRICHOR PVT. LTD." data-testid="footer-legal-input" /></div>
                <div><Label className="text-xs">Fine print (right side)</Label><Input value={footer.fine_print} onChange={(e) => setFooter({...footer, fine_print: e.target.value})} placeholder="All prices in INR. Inclusive of GST." data-testid="footer-fineprint-input" /></div>
                <div className="md:col-span-2"><Label className="text-xs">Copyright text (bottom-left)</Label><Input value={footer.copyright_text} onChange={(e) => setFooter({...footer, copyright_text: e.target.value})} placeholder="2026 Rejumist™. All rights reserved." data-testid="footer-copyright-input" /></div>
                <div><Label className="text-xs">Facebook URL</Label><Input value={footer.social_links?.facebook_url || ''} onChange={(e) => setFooter({...footer, social_links: { ...footer.social_links, facebook_url: e.target.value }})} placeholder="https://www.facebook.com/..." data-testid="footer-facebook-input" /></div>
                <div><Label className="text-xs">Instagram URL</Label><Input value={footer.social_links?.instagram_url || ''} onChange={(e) => setFooter({...footer, social_links: { ...footer.social_links, instagram_url: e.target.value }})} placeholder="https://www.instagram.com/..." data-testid="footer-instagram-input" /></div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold font-heading">Link Columns</h2>
                <Button size="sm" variant="outline" onClick={addFooterColumn} data-testid="add-footer-column-btn"><Plus className="h-4 w-4 mr-1" /> Add Column</Button>
              </div>
              {footer.columns.length === 0 && <p className="text-xs text-muted-foreground">No columns yet. Click "Add Column" to start.</p>}
              {footer.columns.map((col, ci) => (
                <div key={ci} className="p-4 rounded-lg border border-[#E0E0E0] bg-[#FAFAFA] space-y-3" data-testid={`footer-col-${ci}`}>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Column title</Label>
                      <Input value={col.title} onChange={(e) => updateColumnTitle(ci, e.target.value)} placeholder="Shop / Explore / Info" data-testid={`footer-col-title-${ci}`} />
                    </div>
                    <Button size="sm" variant="outline" className="text-destructive hover:bg-destructive/10" onClick={() => removeFooterColumn(ci)} data-testid={`remove-footer-col-${ci}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Remove Column
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {col.links.map((link, li) => (
                      <div key={li} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`footer-link-${ci}-${li}`}>
                        <div className="md:col-span-3">
                          <Input value={link.label} onChange={(e) => updateFooterLink(ci, li, 'label', e.target.value)} placeholder="Link label" data-testid={`footer-link-label-${ci}-${li}`} />
                        </div>
                        <div className="md:col-span-4">
                          <Input value={link.url} onChange={(e) => updateFooterLink(ci, li, 'url', e.target.value)} placeholder="/products or https://..." data-testid={`footer-link-url-${ci}-${li}`} />
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                          <Switch checked={!!link.external} onCheckedChange={(v) => updateFooterLink(ci, li, 'external', v)} data-testid={`footer-link-external-${ci}-${li}`} />
                          <span className="text-[11px] text-muted-foreground">External</span>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                          <Switch checked={!!link.admin_only} onCheckedChange={(v) => updateFooterLink(ci, li, 'admin_only', v)} data-testid={`footer-link-adminonly-${ci}-${li}`} />
                          <span className="text-[11px] text-muted-foreground">Admin only</span>
                        </div>
                        <div className="md:col-span-1 flex items-center justify-end gap-1">
                          <Switch checked={link.visible !== false} onCheckedChange={(v) => updateFooterLink(ci, li, 'visible', v)} data-testid={`footer-link-visible-${ci}-${li}`} />
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeFooterLink(ci, li)} data-testid={`remove-footer-link-${ci}-${li}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addFooterLink(ci)} className="w-full" data-testid={`add-footer-link-${ci}`}>
                      <Plus className="h-3 w-3 mr-1" /> Add Link
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="cartshelf" className="space-y-4">
            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold font-heading">Cart Recommendations Shelf</h2>
                <Button size="sm" onClick={saveCartShelf} data-testid="save-cartshelf-btn"><Save className="mr-2 h-4 w-4" /> Save</Button>
              </div>
              <p className="text-xs text-muted-foreground">Surfaces 2-3 product cards on the cart page when total qty is below the target. Helps customers hit a bulk-discount tier by adding complementary teas. Hidden once cart already meets the target.</p>
              <div className="flex items-center gap-3 mt-2">
                <Switch checked={!!cartShelf.enabled} onCheckedChange={(v) => setCartShelf({...cartShelf, enabled: v})} data-testid="cartshelf-enabled" />
                <span className="text-sm">{cartShelf.enabled ? 'Active (visible on cart)' : 'Hidden'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Heading</Label><Input value={cartShelf.heading} onChange={(e) => setCartShelf({...cartShelf, heading: e.target.value})} placeholder="Frequently bought together" data-testid="cartshelf-heading-input" /></div>
                <div><Label className="text-xs">Sub-heading</Label><Input value={cartShelf.subheading} onChange={(e) => setCartShelf({...cartShelf, subheading: e.target.value})} placeholder="Add another tea to unlock bigger savings." data-testid="cartshelf-subheading-input" /></div>
                <div><Label className="text-xs">Target qty (e.g. 5 for the 5+ tier)</Label><Input type="number" min={2} value={cartShelf.target_qty} onChange={(e) => setCartShelf({...cartShelf, target_qty: e.target.value})} data-testid="cartshelf-targetqty-input" /></div>
                <div className="grid grid-cols-2 gap-3 col-span-full">
                  <div><Label className="text-xs">Basket discount % (applied to every line when the threshold below is met)</Label><Input type="number" min={0} max={90} step="0.5" value={cartShelf.basket_discount_percent} onChange={(e) => setCartShelf({...cartShelf, basket_discount_percent: e.target.value})} placeholder="e.g. 10" data-testid="cartshelf-basketpct-input" /></div>
                  <div><Label className="text-xs">Basket min qty (cart total qty needed to unlock the basket %)</Label><Input type="number" min={0} value={cartShelf.basket_min_qty} onChange={(e) => setCartShelf({...cartShelf, basket_min_qty: e.target.value})} placeholder="e.g. 5" data-testid="cartshelf-basketmin-input" /></div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">CTA template — supports {'{needed}'}, {'{pct}'}, {'{savings}'}</Label>
                  <Input value={cartShelf.cta_template} onChange={(e) => setCartShelf({...cartShelf, cta_template: e.target.value})} placeholder="Add {needed} more to unlock {pct}% off (save ₹{savings})" data-testid="cartshelf-cta-input" />
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-white border border-[#E0E0E0] space-y-3">
              <h3 className="font-heading font-semibold">Featured products (max 4)</h3>
              <p className="text-xs text-muted-foreground">Pick 2-4 products. Out-of-stock or already-in-cart items are filtered automatically.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto">
                {allProducts.map((p) => {
                  const selected = cartShelf.product_ids.includes(p.product_id);
                  const disabled = !selected && cartShelf.product_ids.length >= 4;
                  return (
                    <button
                      key={p.product_id}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleShelfProduct(p.product_id)}
                      className={`p-2 rounded-lg border text-left transition-colors ${selected ? 'border-primary bg-primary/10' : disabled ? 'border-[#E0E0E0] opacity-40 cursor-not-allowed' : 'border-[#E0E0E0] hover:border-primary/40'}`}
                      data-testid={`cartshelf-pick-${p.product_id}`}
                    >
                      <img src={p.image_url} alt={p.name} className="w-full h-20 object-cover rounded mb-1" />
                      <p className="text-[11px] font-medium line-clamp-2">{p.name}</p>
                      <p className="text-[10px] text-muted-foreground">{'\u20B9'}{p.price}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{cartShelf.product_ids.length} / 4 selected</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminCMS;
